// =====================================================================
// Does this browser hold a live microphone?
//
// Verdict on TIME SINCE THE LAST ATTEMPT, never on the rejection. A single rejection
// is not evidence: measured over 14 days, sessions that failed and recovered took a
// median 55 SECONDS to do so, essentially none of it the SDR acting. Gating on the
// first failure would stop about as many working SDRs as broken ones.
//
//   warn    (5s)  — advisory, nothing is taken away
//   blocked (60s) — refuse the pool
//
// Time is also the only thing that catches an acquire that NEVER SETTLES — an
// unanswered permission prompt — which is the incident this exists for.
// =====================================================================

// Chrome 64+, Firefox 132+, Safari 16+ for this descriptor; older engines and any
// query failure land on 'unknown', which is permissive.
export type MicPermission = 'granted' | 'denied' | 'prompt' | 'unknown';

// `blocked` is the only value that gates the pool picker; `warn` only speaks.
export type MicState = 'pending' | 'live' | 'warn' | 'blocked';

export interface MicHealth {
  // Device register, a retry, or a re-check. Null before the Device exists, so a
  // missing voice token cannot masquerade as a dead mic.
  attemptedAt: number | null;
  // A RESOLVED setInputDevice, never a device-list refresh: `devicechange` fires
  // while permission is denied, and reportAudioSnapshot dedupes and returns void.
  acquiredAt: number | null;
  failedAt: number | null;
  lastError: string | null;
  permission: MicPermission;
  // One per episode, reset on acquire — else a mid-shift failure inherits the boot
  // episode's spent retry.
  retried: boolean;
}

export const WARN_MS = 5_000;
export const BLOCK_MS = 60_000;
export const RETRY_AFTER_MS = 2_000;

// Recovering the mic after it was released on purpose. A rejection straight after
// a release is usually the device not yet freed, so only the last rung is allowed
// to stamp — stamping earlier would manufacture an episode out of release lag.
export function micRestoreStep(attempt: number): 'retry' | 'recheck' | 'give_up' {
  if (attempt === 1) return 'retry';
  if (attempt === 2) return 'recheck';
  return 'give_up';
}

export const newMicHealth = (): MicHealth => ({
  attemptedAt: null,
  acquiredAt: null,
  failedAt: null,
  lastError: null,
  permission: 'unknown',
  retried: false,
});

const holdsMic = (h: MicHealth): boolean =>
  h.acquiredAt !== null && (h.failedAt === null || h.failedAt <= h.acquiredAt);

// When the CURRENT unhealthy stretch began — a failure that landed after a healthy
// period, else the attempt that never came back. Anchoring on the attempt alone would
// date a mid-shift failure to boot, so one failed mic pick hours in would arrive
// already past both thresholds and skip the grace the thresholds exist to give.
export function micEpisodeStart(h: MicHealth): number | null {
  if (h.attemptedAt === null) return null;
  return h.failedAt !== null && h.failedAt > h.attemptedAt ? h.failedAt : h.attemptedAt;
}

export function micState(h: MicHealth, now: number): MicState {
  const since = micEpisodeStart(h);
  if (since === null) return 'pending';
  if (holdsMic(h)) return 'live';
  const elapsed = now - since;
  if (elapsed >= BLOCK_MS) return 'blocked';
  if (elapsed >= WARN_MS) return 'warn';
  return 'pending';
}

export interface MicBannerCopy {
  title: string;
  body: string;
}

// One blocked stretch, reported once at each end. Pure so the suppression rules are
// testable: the cockpit has no component-test harness, and "did this emit twice?" is
// exactly the kind of thing that only shows up in production log volume.
export interface MicEpisode {
  // When the current blocked stretch began, or null between them.
  blockedSince: number | null;
  report: 'blocked' | 'recovered' | null;
  blockedMs?: number;
}

export function micEpisode(blockedSince: number | null, state: MicState, now: number): MicEpisode {
  if (state === 'blocked') {
    // Already reported. A blocked SDR stays blocked for hours.
    if (blockedSince !== null) return { blockedSince, report: null };
    return { blockedSince: now, report: 'blocked' };
  }
  if (blockedSince === null) return { blockedSince: null, report: null };
  // A re-check in flight shows as `pending`; that ends the episode without claiming
  // a recovery, so a failing re-check can report the next one instead of being
  // suppressed forever.
  if (state !== 'live') return { blockedSince: null, report: null };
  return { blockedSince: null, report: 'recovered', blockedMs: now - blockedSince };
}

// Names a cause only where the Permissions API stated one. Twilio's rejection cannot
// tell a blocked permission from absent hardware or a device another app holds, and
// telling an SDR to unblock a permission they never denied sends them to fix nothing.
export function micBannerCopy(h: MicHealth, state: MicState): MicBannerCopy | null {
  if (state !== 'warn' && state !== 'blocked') return null;
  // A live call keeps running and an SDR already in a pool stays in it: only the
  // picker is taken away, and only at `blocked`.
  const consequence =
    state === 'blocked'
      ? ' You can’t join a pool until this is fixed.'
      : ' Prospects won’t be able to hear you.';

  if (h.permission === 'denied') {
    return {
      title: 'Your browser is blocking the microphone',
      body: `Click the padlock in the address bar, set Microphone to Allow, then reload this page.${consequence}`,
    };
  }
  if (h.permission === 'prompt') {
    // Deliberately not "click Allow on the prompt": Firefox reports `prompt` for any
    // grant the SDR did not tick "Remember" on, when no prompt is on screen at all.
    return {
      title: 'Your browser hasn’t granted microphone access',
      body: `Allow the microphone for this site — check for a prompt, or the padlock in the address bar — then use Re-check.${consequence}`,
    };
  }
  return {
    title: 'No microphone available',
    body: `Check your mic is plugged in and not in use by another app (Zoom, Meet, Teams), then use Re-check.${consequence}`,
  };
}
